import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuditLogs = ({ token, onBack }) => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [filters, setFilters] = useState({
    action: '',
    userEmail: '',
    success: ''
  });
  const [availableActions, setAvailableActions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchAuditLogs = useCallback(async (page = pagination.currentPage) => {
    setIsLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (filters.action) params.append('action', filters.action);
      if (filters.userEmail) params.append('userEmail', filters.userEmail);
      if (filters.success !== '') params.append('success', filters.success);

      const response = await axios.get(`/api/admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch audit logs');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, filters.action, filters.userEmail, filters.success, token]);

  const fetchAvailableActions = useCallback(async () => {
    try {
      const response = await axios.get('/api/admin/audit-actions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableActions(response.data.actions);
    } catch (error) {
      console.error('Failed to fetch available actions', error);
    }
  }, [token]);

  useEffect(() => {
    fetchAuditLogs();
    fetchAvailableActions();
  }, [fetchAuditLogs, fetchAvailableActions]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setPagination({ ...pagination, currentPage: 1 });
    fetchAuditLogs(1);
  };

  const handleClearFilters = () => {
    setFilters({ action: '', userEmail: '', success: '' });
    setPagination({ ...pagination, currentPage: 1 });
    setTimeout(() => fetchAuditLogs(1), 0);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatEventData = (data) => {
    if (!data) return 'N/A';
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-slate-950 to-slate-900 p-8">
      <div className="mb-8">
        <button onClick={onBack} className="text-blue-400 hover:text-blue-300 font-semibold transition-colors mb-4">
          ← Back to Dashboard
        </button>
        <h2 className="text-3xl font-bold text-white mb-2">Audit Logs</h2>
        <p className="text-slate-400">Security event tracking and monitoring</p>
      </div>

      {error && <div className="text-red-400 text-sm mb-4 bg-red-500 bg-opacity-10 p-3 rounded border border-red-500 border-opacity-20">{error}</div>}

      <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-6 border border-blue-500 border-opacity-10 mb-8">
        <h3 className="text-xl font-semibold mb-6 text-white">Filters</h3>
        <form onSubmit={handleApplyFilters} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-blue-400 mb-2">Action</label>
              <select
                name="action"
                value={filters.action}
                onChange={handleFilterChange}
                className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
              >
                <option value="">All Actions</option>
                {availableActions.map(action => (
                  <option key={action} value={action}>{action}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-blue-400 mb-2">User Email</label>
              <input
                type="text"
                name="userEmail"
                value={filters.userEmail}
                onChange={handleFilterChange}
                placeholder="Search by email..."
                className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-blue-400 mb-2">Status</label>
              <select
                name="success"
                value={filters.success}
                onChange={handleFilterChange}
                className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
              >
                <option value="">All</option>
                <option value="true">Success</option>
                <option value="false">Failed</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3">
            <button type="submit" className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all hover:shadow-lg">Apply Filters</button>
            <button type="button" onClick={handleClearFilters} className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors">Clear</button>
          </div>
        </form>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-6 border border-blue-500 border-opacity-10 text-center">
          <div className="text-3xl font-bold text-blue-400">{pagination.totalUsers}</div>
          <div className="text-slate-400 text-sm mt-2">Total Events</div>
        </div>
        <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-6 border border-blue-500 border-opacity-10 text-center">
          <div className="text-3xl font-bold text-green-400">{logs.filter(l => l.success).length}</div>
          <div className="text-slate-400 text-sm mt-2">Successful (This Page)</div>
        </div>
        <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-6 border border-blue-500 border-opacity-10 text-center">
          <div className="text-3xl font-bold text-red-400">{logs.filter(l => !l.success).length}</div>
          <div className="text-slate-400 text-sm mt-2">Failed (This Page)</div>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-12 border border-blue-500 border-opacity-10 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            <span className="text-slate-400 ml-2">Loading audit logs...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg border border-blue-500 border-opacity-10 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500 border-opacity-10">
                  <th className="text-left px-4 py-3 text-blue-400 font-semibold">Timestamp</th>
                  <th className="text-left px-4 py-3 text-blue-400 font-semibold">Action</th>
                  <th className="text-left px-4 py-3 text-blue-400 font-semibold">User</th>
                  <th className="text-left px-4 py-3 text-blue-400 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 text-blue-400 font-semibold">IP Address</th>
                  <th className="text-left px-4 py-3 text-blue-400 font-semibold">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-slate-400">No audit logs found</td>
                  </tr>
                ) : (
                  logs.map(log => (
                    <React.Fragment key={log.id}>
                      <tr className={`border-b border-blue-500 border-opacity-10 hover:bg-blue-500 hover:bg-opacity-5 transition-colors ${!log.success ? 'bg-red-500 bg-opacity-5' : ''}`}>
                        <td className="px-4 py-3 text-sm text-slate-300">{formatTimestamp(log.timestamp)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            log.action.includes('password') ? 'bg-yellow-500 bg-opacity-20 text-yellow-400' :
                            log.action.includes('unlock') ? 'bg-blue-500 bg-opacity-20 text-blue-400' :
                            log.action.includes('verification') ? 'bg-purple-500 bg-opacity-20 text-purple-400' :
                            log.action.includes('login') ? 'bg-green-500 bg-opacity-20 text-green-400' :
                            log.action.includes('suspicious') ? 'bg-red-500 bg-opacity-20 text-red-400' :
                            'bg-slate-700 text-slate-300'
                          }`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">{log.userEmail || 'N/A'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            log.success
                              ? 'bg-green-500 bg-opacity-20 text-green-400'
                              : 'bg-red-500 bg-opacity-20 text-red-400'
                          }`}>
                            {log.success ? '✓ Success' : '✗ Failed'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">{log.ipAddress || 'N/A'}</td>
                        <td className="px-4 py-3">
                          <button
                            className="text-blue-400 hover:text-blue-300 font-semibold transition-colors text-sm"
                            onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                          >
                            {expandedLog === log.id ? 'Hide' : 'Show'}
                          </button>
                        </td>
                      </tr>
                      {expandedLog === log.id && (
                        <tr className="bg-slate-800 bg-opacity-30 border-b border-blue-500 border-opacity-10">
                          <td colSpan="6" className="px-4 py-4">
                            <div className="space-y-3 text-sm">
                              <div>
                                <strong className="text-blue-400">Tenant ID:</strong> <span className="text-slate-300">{log.tenantId || 'N/A'}</span>
                              </div>
                              <div>
                                <strong className="text-blue-400">User Agent:</strong> <span className="text-slate-300 break-all">{log.userAgent || 'N/A'}</span>
                              </div>
                              {log.eventData && (
                                <div>
                                  <strong className="text-blue-400">Event Data:</strong>
                                  <pre className="bg-slate-900 bg-opacity-50 p-3 rounded mt-2 overflow-x-auto text-xs text-slate-300">{formatEventData(log.eventData)}</pre>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={() => {
                  setPagination({ ...pagination, currentPage: pagination.currentPage - 1 });
                }}
                disabled={!pagination.hasPrevPage}
                className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <span className="text-slate-300 font-semibold">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => {
                  setPagination({ ...pagination, currentPage: pagination.currentPage + 1 });
                }}
                disabled={!pagination.hasNextPage}
                className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLogs;
